import { useState, useEffect } from 'react';

/**
 * Hook pour gérer le mode sombre
 * @param key - Clé localStorage (default: 'darkMode')
 * @returns [isDark, toggleDark]
 */
export function useDarkMode(key: string = 'darkMode'): [boolean, () => void] {
  const [isDark, setIsDark] = useState<boolean>(() => {
    // Initialiser depuis localStorage
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      return JSON.parse(stored);
    }
    // Sinon, activer le mode sombre par défaut
    return true;
  });

  useEffect(() => {
    // Appliquer la classe dark-mode sur body
    if (isDark) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }

    // Sauvegarder dans localStorage
    localStorage.setItem(key, JSON.stringify(isDark));
  }, [isDark, key]);

  // Listen for dark-mode-change events (from backup restore)
  useEffect(() => {
    const handleDarkModeChange = (event: CustomEvent<boolean>) => {
      setIsDark(event.detail);
    };

    window.addEventListener('dark-mode-change', handleDarkModeChange as EventListener);
    return () => {
      window.removeEventListener('dark-mode-change', handleDarkModeChange as EventListener);
    };
  }, []);

  const toggleDark = (): void => {
    setIsDark(prev => !prev);
  };

  return [isDark, toggleDark];
}
